import { escapeHtml } from './escapeHtml.js';

/** Slug file in `public/teams/{slug}.webp` — stemmi ufficiali Lega Serie A (media-sdp). */
const TEAM_SLUG_BY_KEY = {
  atalanta: 'atalanta',
  bologna: 'bologna',
  cagliari: 'cagliari',
  como: 'como',
  cremonese: 'cremonese',
  fiorentina: 'fiorentina',
  frosinone: 'frosinone',
  genoa: 'genoa',
  'hellas verona': 'hellas-verona',
  verona: 'hellas-verona',
  inter: 'inter',
  juventus: 'juventus',
  lazio: 'lazio',
  lecce: 'lecce',
  milan: 'milan',
  monza: 'monza',
  napoli: 'napoli',
  parma: 'parma',
  pisa: 'pisa',
  roma: 'roma',
  sassuolo: 'sassuolo',
  torino: 'torino',
  udinese: 'udinese',
  venezia: 'venezia',
};

export function normalizeTeamKey(team) {
  return String(team || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/\s+/g, ' ');
}

/**
 * @returns {string|null} slug per `public/teams/{slug}.webp`
 */
export function teamNameToSlug(team) {
  const key = normalizeTeamKey(team);
  if (!key || key === 'n/a') return null;
  return TEAM_SLUG_BY_KEY[key] || null;
}

/**
 * @returns {string|null}
 */
export function getTeamLogoUrl(team) {
  const slug = teamNameToSlug(team);
  return slug ? `/teams/${slug}.webp` : null;
}

/**
 * HTML logo squadra per celle tabella (nome in alt/title).
 * @param {string} team
 * @param {{ size?: number, className?: string }} [opts]
 */
export function buildTeamLogoHtml(team, opts = {}) {
  const size = opts.size ?? 22;
  const className = opts.className ?? 'team-logo';
  const name = String(team || '').trim() || 'N/A';
  const src = getTeamLogoUrl(team);

  if (src) {
    return `<img class="${className}" src="${escapeHtml(src)}" width="${size}" height="${size}" alt="${escapeHtml(name)}" title="${escapeHtml(name)}" loading="lazy" decoding="async" />`;
  }

  const initials = name.slice(0, 3).toUpperCase();
  return `<span class="team-logo-fallback" title="${escapeHtml(name)}" aria-label="${escapeHtml(name)}">${escapeHtml(initials)}</span>`;
}

/**
 * Crea elemento DOM logo (renderer legacy).
 */
export function createTeamLogoElement(team, opts = {}) {
  const size = opts.size ?? 22;
  const name = String(team || '').trim() || 'N/A';
  const src = getTeamLogoUrl(team);
  if (src) {
    const img = document.createElement('img');
    img.className = opts.className ?? 'team-logo';
    img.src = src;
    img.width = size;
    img.height = size;
    img.alt = name;
    img.title = name;
    img.loading = 'lazy';
    img.decoding = 'async';
    return img;
  }
  const span = document.createElement('span');
  span.className = 'team-logo-fallback';
  span.title = name;
  span.setAttribute('aria-label', name);
  span.textContent = name.slice(0, 3).toUpperCase();
  return span;
}

const PLAYER_NAME_LOGO_SIZE = 22;

/**
 * Cella nome con logo squadra allineato (slot fisso 22×22).
 */
export function buildPlayerNameCellHtml(team, name, opts = {}) {
  const displayName = String(name || '').trim() || 'N/A';
  const teamName = String(team || '').trim();
  const cellClass =
    opts.variant === 'card' ? 'player-name-cell player-name-cell--card' : 'player-name-cell';
  const textClass = opts.variant === 'card' ? 'player-name-text pc-name' : 'player-name-text';
  const logoSize = opts.logoSize ?? (opts.variant === 'card' ? 20 : PLAYER_NAME_LOGO_SIZE);
  const logoClass =
    opts.variant === 'card' ? 'team-logo team-logo--card' : 'team-logo team-logo--inline';
  const logoHtml = buildTeamLogoHtml(team, {
    size: logoSize,
    className: logoClass,
  });

  return `<span class="${cellClass}" title="${escapeHtml(displayName)}"><span class="player-name-logo" aria-hidden="true">${logoHtml}</span><span class="${textClass}">${escapeHtml(displayName)}</span></span>`;
}

/**
 * Elemento DOM per cella nome + logo (renderer legacy).
 */
export function createPlayerNameCellElement(team, name, opts = {}) {
  const displayName = String(name || '').trim() || 'N/A';
  const wrap = document.createElement('span');
  wrap.className = 'player-name-cell';
  wrap.title = displayName;

  const logoSlot = document.createElement('span');
  logoSlot.className = 'player-name-logo';
  logoSlot.setAttribute('aria-hidden', 'true');
  logoSlot.appendChild(
    createTeamLogoElement(team, {
      size: opts.logoSize ?? PLAYER_NAME_LOGO_SIZE,
      className: 'team-logo team-logo--inline',
    }),
  );

  const text = document.createElement('span');
  text.className = 'player-name-text';
  text.textContent = displayName;

  wrap.appendChild(logoSlot);
  wrap.appendChild(text);
  return wrap;
}
